'use client'

import * as React from 'react'
import Image from 'next/image'
import { Button } from '@repo/design-system-cf/components/button'
import { tw } from 'utils/classnames'
import { requestIdleCallbackPonyfill } from 'utils/dom'

import { InfiniteCarousel } from '../infinite-carousel'
import { FEATURE_CARDS } from './feature-cards-data'
import type { FeatureCard } from './feature-cards-data'

const STUDIO_BASE_URL = process.env.NEXT_PUBLIC_STUDIO_CF_URL ?? ''
const LFS_VIDEO_BASE = `${process.env.NEXT_PUBLIC_LFS_URL}/studio/homepage/videos`

export interface LandscapeSliderTexts {
  heading: string
  get_started: string
  scroll_left: string
  scroll_right: string
  browse_label: string
  create_label: string
  cards: Record<string, { title: string; description: string }>
}

export function LandscapeSlider({ texts }: { texts: LandscapeSliderTexts }) {
  const loopedCards = React.useMemo(
    () => [...FEATURE_CARDS, ...FEATURE_CARDS, ...FEATURE_CARDS],
    [],
  )

  return (
    <InfiniteCarousel
      itemCount={FEATURE_CARDS.length}
      scrollLeftLabel={texts.scroll_left}
      scrollRightLabel={texts.scroll_right}
      arrowTopClassName="top-[104px] lg:top-[126px] -translate-y-1/2"
    >
      {loopedCards.map((card, index) => {
        const setIndex = Math.floor(index / FEATURE_CARDS.length)
        return (
          <FeatureCardItem
            key={`${card.id}-${setIndex}`}
            data-card
            card={card}
            texts={texts}
            isLCP={index <= 1}
          />
        )
      })}
    </InfiniteCarousel>
  )
}

function FeatureCardItem({
  card,
  texts,
  isLCP,
  ...rest
}: {
  card: FeatureCard
  texts: LandscapeSliderTexts
  isLCP: boolean
  'data-card'?: true
}) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [isInView, setIsInView] = React.useState(false)
  const prefersReducedMotion = usePrefersReducedMotion()
  const cardTexts = texts.cards[card.id]

  const posterUrl = `${LFS_VIDEO_BASE}/${card.posterFileName}`
  const videoUrl = `${LFS_VIDEO_BASE}/${card.videoFileName}`

  React.useEffect(() => {
    const el = containerRef.current
    if (!el) {
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Defer video loading: start fetching only after idle to avoid competing
  // with LCP poster images for bandwidth.
  React.useEffect(() => {
    const video = videoRef.current
    if (!video || !isInView || prefersReducedMotion) {
      return
    }
    requestIdleCallbackPonyfill(() => {
      video.preload = 'auto'
      void video.play().catch(() => {})
    })
  }, [isInView, prefersReducedMotion])

  const handleMouseEnter = () => {
    if (prefersReducedMotion) {
      void videoRef.current?.play().catch(() => {})
    }
  }

  const handleMouseLeave = () => {
    if (prefersReducedMotion && videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }

  return (
    <div
      ref={containerRef}
      className="shrink-0 w-[312px] lg:w-[380px] flex flex-col items-start group relative snap-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      <a
        href={`${STUDIO_BASE_URL}${card.studioPath}`}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute inset-0 z-[2]"
        aria-label={cardTexts?.title}
      />
      <div className="aspect-[380/253] w-full mb-2 rounded-lg overflow-hidden relative flex items-center justify-center">
        {isInView ? (
          <video
            ref={videoRef}
            src={videoUrl}
            poster={posterUrl}
            preload="none"
            loop
            muted
            playsInline
            className="absolute inset-0 size-full object-cover pointer-events-none"
            onError={e => e.stopPropagation()}
          />
        ) : (
          <Image
            src={posterUrl}
            alt=""
            fill
            sizes="(min-width: 1024px) 380px, 312px"
            className="object-cover pointer-events-none"
            priority={isLCP}
            loading={isLCP ? 'eager' : 'lazy'}
            fetchPriority={isLCP ? 'high' : undefined}
          />
        )}
        <div className="absolute inset-[-1px] bg-cf-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-[250ms]" />
        <Button
          variant="primary-inverse"
          size="regular"
          tabIndex={-1}
          aria-hidden="true"
          className="relative z-[1] -translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-[250ms] ease-out pointer-events-none"
        >
          {texts.get_started}
        </Button>
      </div>
      <span className="text-14 font-bold w-full">{cardTexts?.title}</span>
      <span className="text-12 text-cf-grey-600 truncate w-full">{cardTexts?.description}</span>
    </div>
  )
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false)

  React.useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mql.matches)

    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return prefersReducedMotion
}
